import React, { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import classNames from 'classnames';
import { FaCreditCard, FaSpinner } from 'react-icons/fa';
import Toast from '../Toast';

interface CheckoutFormProps {
  planId: string;
  onSubscriptionComplete: (result: { success: boolean; subscriptionId?: string }) => void;
  buttonText?: string;
  isChangingPlan?: boolean;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Inter", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  planId,
  onSubscriptionComplete,
  buttonText = 'Suscribirse',
  isChangingPlan = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('No se pudo cargar el formulario de pago');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Create payment method
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (paymentMethodError) {
        setError(paymentMethodError.message || 'Error al procesar el pago');
        setLoading(false);
        return;
      }

      // Call API to create subscription with payment method
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/subscription`, {
        method: isChangingPlan ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          planId,
          paymentMethodId: paymentMethod.id,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Error al crear la suscripción');
      }

      // Handle subscription activation
      if (result.data?.clientSecret) {
        // Confirm the payment if required
        const { error: confirmError } = await stripe.confirmCardPayment(result.data.clientSecret);
        
        if (confirmError) {
          throw new Error(confirmError.message || 'Error al confirmar el pago');
        }
      }

      // Success
      setSuccessMessage('¡Suscripción activada correctamente!');
      onSubscriptionComplete({ 
        success: true, 
        subscriptionId: result.data?.subscriptionId 
      });
    } catch (err) {
      console.error('Subscription error:', err);
      setError((err as Error).message || 'Ocurrió un error al procesar tu suscripción');
      onSubscriptionComplete({ success: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {successMessage && (
        <Toast
          message={successMessage}
          type="success"
          onClose={() => setSuccessMessage(null)}
        />
      )}
      
      {error && (
        <Toast
          message={error}
          type="error"
          onClose={() => setError(null)}
        />
      )}

      <div className="p-4 border rounded-lg bg-white shadow-sm">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Información de tarjeta
          </label>
          <div className="border p-3 rounded-md">
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || loading}
        className={classNames(
          'w-full flex justify-center items-center gap-2 py-3 px-4 rounded-md text-white font-medium',
          {
            'bg-blue-600 hover:bg-blue-700': !loading,
            'bg-blue-400 cursor-not-allowed': loading,
          }
        )}
      >
        {loading ? (
          <>
            <FaSpinner className="animate-spin" />
            Procesando...
          </>
        ) : (
          <>
            <FaCreditCard />
            {buttonText}
          </>
        )}
      </button>
    </form>
  );
};

export default CheckoutForm; 